import cn from 'classnames'
import Link from 'next/link'
import type {FC} from 'react'
import {useEffect, useState} from 'react'

type PaginationControlsProps = {
  totalPages: number
  currentPage: number
  baseUrl: string
  activeCategory?: string
  onScroll: () => void
  onPageChange?: (page: number) => void
}

const PAGES_PER_VIEW = 5

export const PaginationControls: FC<PaginationControlsProps> = ({
  totalPages,
  currentPage,
  baseUrl,
  activeCategory,
  onScroll,
  onPageChange,
}) => {
  const [carouselPage, setCarouselPage] = useState(0)

  // Calculate which carousel page the current page is on
  useEffect(() => {
    const newCarouselPage = Math.floor((currentPage - 1) / PAGES_PER_VIEW)
    setCarouselPage(newCarouselPage)
  }, [currentPage])

  if (totalPages <= 1) {
    return null
  }

  const totalCarouselPages = Math.ceil(totalPages / PAGES_PER_VIEW)
  const startPage = carouselPage * PAGES_PER_VIEW + 1
  const endPage = Math.min(startPage + PAGES_PER_VIEW - 1, totalPages)

  const buildPageUrl = (pageNumber: number): string => {
    if (pageNumber === 1) {
      return activeCategory ? `${baseUrl}/category/${activeCategory}` : baseUrl
    }

    return activeCategory ? `${baseUrl}/category/${activeCategory}/${pageNumber}` : `${baseUrl}/page/${pageNumber}`
  }

  const handlePrevious = () => {
    if (carouselPage > 0) {
      setCarouselPage((prev) => prev - 1)
    }
  }

  const handleNext = () => {
    if (carouselPage < totalCarouselPages - 1) {
      setCarouselPage((prev) => prev + 1)
    }
  }

  const canGoPrevious = carouselPage > 0
  const canGoNext = carouselPage < totalCarouselPages - 1

  return (
    <div className="flex w-full min-w-0 items-center justify-center gap-2">
      <button
        onClick={handlePrevious}
        disabled={!canGoPrevious}
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-4 border border-solid border-black/10 transition-colors',
          {
            'cursor-pointer bg-moss-white hover:bg-beige-100': canGoPrevious,
            'cursor-not-allowed bg-moss-white/50 opacity-50': !canGoPrevious,
          },
        )}
        aria-label="Previous pages"
        type="button"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M10 12L6 8L10 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Page Numbers - All pages rendered for SEO, visibility controlled by CSS */}
      <div className="flex shrink-0 gap-2">
        {Array.from({length: totalPages}, (_, i) => {
          const pageNumber = i + 1
          const href = buildPageUrl(pageNumber)
          const isInCarouselWindow = pageNumber >= startPage && pageNumber <= endPage

          const pageClasses = cn(
            'box-border flex size-8 shrink-0 flex-col items-center justify-center rounded-4 border border-solid border-black/10 px-4 py-3',
            {
              'bg-green-dark': currentPage === pageNumber,
              'bg-moss-white': currentPage !== pageNumber,
              hidden: !isInCarouselWindow,
            },
          )

          const pageLabel = (
            <div className="flex flex-col justify-center leading-[0]">
              <p
                className={cn('text-detail text-center leading-normal', {
                  'text-moss-white': currentPage === pageNumber,
                  'text-black': currentPage !== pageNumber,
                })}
              >
                {String(pageNumber).padStart(2, '0')}
              </p>
            </div>
          )

          if (onPageChange) {
            return (
              <button
                key={pageNumber}
                type="button"
                onClick={() => {
                  onPageChange(pageNumber)
                  onScroll()
                }}
                className={pageClasses}
              >
                {pageLabel}
              </button>
            )
          }

          return (
            <Link key={pageNumber} href={href} scroll={false} onClick={onScroll} className={pageClasses}>
              {pageLabel}
            </Link>
          )
        })}
      </div>

      <button
        onClick={handleNext}
        disabled={!canGoNext}
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-4 border border-solid border-black/10 transition-colors',
          {
            'cursor-pointer bg-moss-white hover:bg-beige-100': canGoNext,
            'cursor-not-allowed bg-moss-white/50 opacity-50': !canGoNext,
          },
        )}
        aria-label="Next pages"
        type="button"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  )
}
